import { CourseId } from '../types';

export interface QuizQuestion {
    id: string;
    text: string;
    options: string[];
    correctIndex: number;
    explanation: string;
}

export const COURSE_QUIZZES: Record<CourseId, QuizQuestion[]> = {
    'foundation': [
        {
            id: 'f1',
            text: "Which of these is the correct way to declare a variable that CANNOT be changed later?",
            options: ["let x = 10", "const x = 10", "var x = 10", "x = 10"],
            correctIndex: 1,
            explanation: "`const` declares a constant variable that cannot be reassigned."
        },
        {
            id: 'f2',
            text: "In an inventory system, which logic structure would you use to check if stock is low?",
            options: ["Loop (for)", "Conditional (if/else)", "Array", "Object"],
            correctIndex: 1,
            explanation: "`if/else` is used to make decisions based on conditions (e.g., if stock < 10)."
        },
        {
            id: 'f3',
            text: "What is an Array used for?",
            options: ["Storing a single number", "Storing a list of multiple items", "Defining a function", "Styling a webpage"],
            correctIndex: 1,
            explanation: "Arrays are ordered lists used to store multiple values in a single variable."
        },
        {
            id: 'f4',
            text: "Does a computer understand human language directly?",
            options: ["Yes, perfectly", "Only English", "No, it needs code to translate instructions into machine language", "Yes, if you speak slowly"],
            correctIndex: 2,
            explanation: "Computers operate on binary code. Programming languages act as a translator between human intent and machine execution."
        },
        {
            id: 'f5',
            text: "Which of these is a 'String' data type?",
            options: ["42", "true", "\"Hello World\"", "undefined"],
            correctIndex: 2,
            explanation: "Strings are sequences of characters wrapped in quotes."
        },
        {
            id: 'f6',
            text: "What is the result of 10 % 3 (Modulo operator)?",
            options: ["3.33", "1", "3", "10"],
            correctIndex: 1,
            explanation: "Modulo (%) returns the remainder of division. 10 divided by 3 is 3 with a remainder of 1."
        },
        {
            id: 'f7',
            text: "Why do we use functions in programming?",
            options: ["To make code harder to read", "To reuse code and keep it organized", "To slow down the computer", "To increase file size"],
            correctIndex: 1,
            explanation: "Functions allow us to write a block of code once and reuse it multiple times, keeping our programs clean and efficient."
        },
        {
            id: 'f8',
            text: "Which operator is used to check if two values are equal?",
            options: ["=", "==", "+", "!"],
            correctIndex: 1,
            explanation: "`=` is for assignment, `==` (or `===`) is for comparison."
        },
        {
            id: 'f9',
            text: "What represents a 'Boolean' value?",
            options: ["\"True\"", "1", "true or false", "null"],
            correctIndex: 2,
            explanation: "Booleans represents logical entities and can only have two values: true or false."
        },
        {
            id: 'f10',
            text: "What is the first index of an array in JavaScript?",
            options: ["1", "0", "-1", "10"],
            correctIndex: 1,
            explanation: "Arrays are zero-indexed, meaning the first item is at position 0."
        }
    ],
    'production': [
        {
            id: 'p1',
            text: "In a Relational Database, what connects a key in one table to another?",
            options: ["Primary Key only", "Foreign Key", "JSON Object", "API Endpoint"],
            correctIndex: 1,
            explanation: "A Foreign Key in one table points to a Primary Key in another, creating a relationship."
        },
        {
            id: 'p2',
            text: "Which SQL command is used to retrieve data from a database?",
            options: ["INSERT", "DELETE", "SELECT", "UPDATE"],
            correctIndex: 2,
            explanation: "`SELECT` is the standard command to query and retrieve data."
        },
        {
            id: 'p3',
            text: "What is the purpose of an API?",
            options: ["To design logos", "To allow different software systems to communicate", "To store passwords", "To encrypt data"],
            correctIndex: 1,
            explanation: "APIs (Application Programming Interfaces) allow different applications to talk to each other."
        },
        {
            id: 'p4',
            text: "What does JSON stand for?",
            options: ["JavaScript Object Notation", "Java System Online Network", "Just Some Ordinary Notes", "Join Server On Network"],
            correctIndex: 0,
            explanation: "JSON is a standard text-based format for representing structured data based on JavaScript object syntax."
        },
        {
            id: 'p5',
            text: "Which HTTP method is typically used to create NEW data?",
            options: ["GET", "POST", "DELETE", "PATCH"],
            correctIndex: 1,
            explanation: "POST requests are used to send data to a server to create/update a resource."
        },
        {
            id: 'p6',
            text: "What does a 404 error code mean?",
            options: ["Success", "Server Error", "Not Found", "Unauthorized"],
            correctIndex: 2,
            explanation: "404 indicates that the server cannot find the requested resource."
        },
        {
            id: 'p7',
            text: "In a database, what is a 'row' often called?",
            options: ["Attribute", "Record", "Field", "Column"],
            correctIndex: 1,
            explanation: "A row in a database table represents a single, implicitly structured data item, often called a record."
        },
        {
            id: 'p8',
            text: "What is the main benefit of a Relational Database?",
            options: ["It uses no structure", "It stores everything in one file", "Data integrity and efficient relationships between data", "It is only for small apps"],
            correctIndex: 2,
            explanation: "Relational databases enforce data structure and relationships, ensuring consistency and efficiency."
        },
        {
            id: 'p9',
            text: "Which SQL clause is used to filter results?",
            options: ["FILTER", "WHERE", "WHEN", "IF"],
            correctIndex: 1,
            explanation: "The WHERE clause is used to extract records that fulfill a specified condition."
        },
        {
            id: 'p10',
            text: "What is 'Backend' development primarily concerned with?",
            options: ["Colors and fonts", "Data, servers, and logic invisible to the user", "The web browser", "CSS animations"],
            correctIndex: 1,
            explanation: "Backend development focuses on server-side logic, databases, and APIs."
        }
    ],
    'cyber-security': [
        {
            id: 'c1',
            text: "What does the 'A' in the CIA Triad stand for?",
            options: ["Authentication", "Authorization", "Availability", "Accounting"],
            correctIndex: 2,
            explanation: "CIA stands for Confidentiality, Integrity, and Availability."
        },
        {
            id: 'c2',
            text: "In 'Defense in Depth', why do we use multiple layers of security?",
            options: ["It's cheaper", "So if one layer fails, others still protect the data", "To make it slower", "To annoy users"],
            correctIndex: 1,
            explanation: "Redundancy ensures that a single failure doesn't result in a total breach."
        },
        {
            id: 'c3',
            text: "Which encryption type is theoretically resistant to future Quantum Computer attacks?",
            options: ["Caesar Cipher", "AES-256 (Standard)", "Lattice-based Cryptography", "ROT13"],
            correctIndex: 2,
            explanation: "Lattice-based algorithms are considered Quantum-Safe because they rely on math problems even quantum computers find hard."
        },
        {
            id: 'c4',
            text: "What is Phishing?",
            options: ["Catching fish", "Hacking a firewall directly", "Tricking a human into revealing sensitive info", "Running a virus scan"],
            correctIndex: 2,
            explanation: "Phishing attacks the human element (Social Engineering), not the code directly."
        },
        {
            id: 'c5',
            text: "What is a 'Zero Day' vulnerability?",
            options: ["A bug fixed immediately", "A flaw known to hackers but not the developers", "A virus that lasts zero days", "A secure software"],
            correctIndex: 1,
            explanation: "It refers to a hole in software that is unknown to the vendor. This security hole is then exploited by hackers."
        },
        {
            id: 'c6',
            text: "Which of these is a strong password practice?",
            options: ["Using '123456'", "Reusing the same password everywhere", "Using a long passphrase with mixed characters", "Writing it on a sticky note"],
            correctIndex: 2,
            explanation: "Length and complexity are key to resisting brute-force attacks."
        },
        {
            id: 'c7',
            text: "What is Multi-Factor Authentication (MFA)?",
            options: ["Logging in twice", "Requiring more than one piece of evidence to verify identity", "Sharing your password", "Using a long username"],
            correctIndex: 1,
            explanation: "MFA requires something you know (password) and something you have (phone/token)."
        },
        {
            id: 'c8',
            text: "What does HTTPS encrypt?",
            options: ["Nothing", "Only images", "Data sent between your browser and the website", "Your hard drive"],
            correctIndex: 2,
            explanation: "HTTPS ensures that data in transit is encrypted and cannot be read by eavesdroppers."
        },
        {
            id: 'c9',
            text: "What is Ransomware?",
            options: ["Free software", "Malware that locks your data and demands payment", "Antivirus software", "A firewall tool"],
            correctIndex: 1,
            explanation: "Ransomware encrypts a user's files and demands a ransom for the decryption key."
        },
        {
            id: 'c10',
            text: "What is the purpose of a Firewall?",
            options: ["To cool down the server", "To burn viruses", "To monitor and control incoming/outgoing network traffic", "To speed up internet"],
            correctIndex: 2,
            explanation: "Firewalls act as a barrier between a trusted network and an untrusted network."
        }
    ],
    'logic-architect': [
        {
            id: 'la1',
            text: "In React, what is a 'Component'?",
            options: ["A database query", "A reusable piece of UI", "A CSS animation", "An API endpoint"],
            correctIndex: 1,
            explanation: "Components are the building blocks of React apps - independent, reusable pieces that describe part of the UI."
        },
        {
            id: 'la2',
            text: "What does the 'useState' hook return?",
            options: ["Just the current value", "A function to update state", "An array with [value, setValue]", "An object with { state, dispatch }"],
            correctIndex: 2,
            explanation: "`useState` returns an array: the current state value and a function to update it, e.g. `const [count, setCount] = useState(0)`."
        },
        {
            id: 'la3',
            text: "When does `useEffect` run by default (no dependency array)?",
            options: ["Only on first render", "After every render", "Only when clicked", "Never automatically"],
            correctIndex: 1,
            explanation: "Without a dependency array, `useEffect` runs after every render (mount and update)."
        },
        {
            id: 'la4',
            text: "How do you pass data FROM a parent component TO a child component?",
            options: ["Using Redux", "Through the browser URL", "Via Props", "By calling setState on the child"],
            correctIndex: 2,
            explanation: "Props (properties) are how data flows downward in React - from parent to child components."
        },
        {
            id: 'la5',
            text: "What happens when you call a state setter function like `setCount(count + 1)`?",
            options: ["Nothing, state is immutable", "React schedules a re-render with the new value", "The page immediately refreshes", "It directly changes the DOM"],
            correctIndex: 1,
            explanation: "Calling a state setter tells React to re-render the component with the new state value. React updates the DOM efficiently."
        },
        {
            id: 'la6',
            text: "What is JSX?",
            options: ["A new programming language", "A syntax extension for JavaScript that looks like HTML", "A database format", "A CSS preprocessor"],
            correctIndex: 1,
            explanation: "JSX allows us to write HTML-like logic directly within JavaScript."
        },
        {
            id: 'la7',
            text: "What is the 'Virtual DOM'?",
            options: ["A VR game", "A lightweight copy of the real DOM used for performance", "A database for React", "A browser plugin"],
            correctIndex: 1,
            explanation: "React uses a Virtual DOM to minimize direct manipulation of the actual DOM, improving performance."
        },
        {
            id: 'la8',
            text: "Can a React component return multiple root elements without a wrapper?",
            options: ["Yes", "No, they must be wrapped (e.g., in a Fragment <>...</>)", "Only loops", "Only if they are divs"],
            correctIndex: 1,
            explanation: "React components must return a single parent element, or use a Fragment (`<>`) to group multiple elements."
        },
        {
            id: 'la9',
            text: "What is the 'children' prop?",
            options: ["A list of child processes", "Content passed between the opening and closing tags of a component", "A variable for loops", "A specific HTML tag"],
            correctIndex: 1,
            explanation: "`children` is a special prop that allows you to pass elements directly into a component."
        },
        {
            id: 'la10',
            text: "Where should you usually call Hooks like `useState`?",
            options: ["Inside loops", "Inside if statements", "At the top level of a function component", "Outside the component"],
            correctIndex: 2,
            explanation: "Hooks must be called at the top level to ensure they run in the same order every render."
        }
    ],
    'digital-foundations': [
        {
            id: 'df1',
            text: "What is the FIRST thing you should check before clicking a link in an email?",
            options: ["If it looks interesting", "The sender's email address and link destination", "If your friend sent it", "The email subject line"],
            correctIndex: 1,
            explanation: "Always verify the sender and hover over links to check where they actually lead before clicking."
        },
        {
            id: 'df2',
            text: "What is your 'digital footprint'?",
            options: ["Your computer's physical location", "The trail of data you leave online", "Your password strength", "Your internet speed"],
            correctIndex: 1,
            explanation: "Your digital footprint includes all the information about you that exists online - posts, searches, photos, and more."
        },
        {
            id: 'df3',
            text: "When researching online, which source is MOST reliable?",
            options: ["The first search result", "A random blog post", "An official government or educational (.gov/.edu) website", "A social media post with many likes"],
            correctIndex: 2,
            explanation: "Official sources like .gov and .edu websites are generally more trustworthy because they have accountability standards."
        },
        {
            id: 'df4',
            text: "Why should you keep your software updated?",
            options: ["To make it look better", "Updates often contain critical security patches", "To use more memory", "To change the icon"],
            correctIndex: 1,
            explanation: "Updates fix security holes that hackers could otherwise exploit."
        },
        {
            id: 'df5',
            text: "What is 'Two-Factor Authentication' (2FA)?",
            options: ["Using two passwords", "Verifying identity with a password + a second step (like a phone code)", "Logging in twice", "Sharing an account"],
            correctIndex: 1,
            explanation: "2FA adds a layer of security by requiring a second form of verification."
        },
        {
            id: 'df6',
            text: "Is it safe to use the same password for every website?",
            options: ["Yes, it's easier to remember", "No, if one site is hacked, all your accounts are at risk", "Only for important sites", "Yes, if it includes a number"],
            correctIndex: 1,
            explanation: "Password reuse is a major security risk. Use unique passwords (and a manager) for each site."
        },
        {
            id: 'df7',
            text: "What does a 'Padlock' icon in the browser address bar mean?",
            options: ["The site is 100% safe", "The connection is encrypted (HTTPS)", "The site is locked", "You cannot leave the site"],
            correctIndex: 1,
            explanation: "It indicates an encrypted connection between you and the server, protecting data in transit."
        },
        {
            id: 'df8',
            text: "What is 'Netiquette'?",
            options: ["Internet speed", "Correct code syntax", "Proper manners and behavior online", "A type of virus"],
            correctIndex: 2,
            explanation: "Netiquette refers to the code of polite behavior on the internet."
        },
        {
            id: 'df9',
            text: "What usually happens if you delete a file on your computer?",
            options: ["It is gone forever instantly", "It goes to the Recycle Bin/Trash first", "It sends an email", "It prints a copy"],
            correctIndex: 1,
            explanation: "Most OSs move deleted files to a holding area (Trash/Recycle Bin) before permanent deletion."
        },
        {
            id: 'df10',
            text: "Which of these is Personal Identifiable Information (PII)?",
            options: ["Your favorite color", "Your full home address", "The weather", "A random number"],
            correctIndex: 1,
            explanation: "PII is any data that could identify a specific individual. Home addresses are sensitive."
        }
    ],
    'data-detective': [
        {
            id: 'dd1',
            text: "Which SQL command retrieves data from a database?",
            options: ["INSERT", "UPDATE", "SELECT", "DELETE"],
            correctIndex: 2,
            explanation: "SELECT is the fundamental command for querying data from database tables."
        },
        {
            id: 'dd2',
            text: "What does the WHERE clause do in SQL?",
            options: ["Orders the results", "Creates a new table", "Filters rows based on conditions", "Counts the total rows"],
            correctIndex: 2,
            explanation: "WHERE filters which rows are included in the result based on specified conditions."
        },
        {
            id: 'dd3',
            text: "What SQL function would you use to count how many items are in a category?",
            options: ["SUM()", "COUNT()", "MAX()", "MIN()"],
            correctIndex: 1,
            explanation: "COUNT() returns the number of rows that match the query criteria."
        },
        {
            id: 'dd4',
            text: "What does a JOIN do in SQL?",
            options: ["Deletes duplicate data", "Combines rows from two or more tables", "Creates a backup", "Sorts data alphabetically"],
            correctIndex: 1,
            explanation: "JOINs connect related data across multiple tables based on matching columns."
        },
        {
            id: 'dd5',
            text: "What is a 'Primary Key'?",
            options: ["The first column", "A unique identifier for each record in a table", " The most important data", "A password"],
            correctIndex: 1,
            explanation: "A Primary Key uniquely identifies each row (like a Social Security Number for data)."
        },
        {
            id: 'dd6',
            text: "Which command sorts the results?",
            options: ["SORT BY", "ORDER BY", "ARRANGE", "ALIGN"],
            correctIndex: 1,
            explanation: "ORDER BY is used to sort the result-set in ascending or descending order."
        },
        {
            id: 'dd7',
            text: "What does 'NULL' represent in a database?",
            options: ["Zero", "A space character", "Missing or undefined data", "An error"],
            correctIndex: 2,
            explanation: "NULL indicates a field with no value."
        },
        {
            id: 'dd8',
            text: "How do you select ALL columns from a table?",
            options: ["SELECT ALL", "SELECT *", "SELECT EACH", "GET ALL"],
            correctIndex: 1,
            explanation: "The asterisk (*) is a wildcard character that selects all columns."
        },
        {
            id: 'dd9',
            text: "Which function gives the total of a numeric column?",
            options: ["TOTAL()", "ADD()", "SUM()", "PLUS()"],
            correctIndex: 2,
            explanation: "SUM() adds up all the values in the specified column."
        },
        {
            id: 'dd10',
            text: "In SQL, how do you handle text values?",
            options: ["Just type them", "Wrap them in single quotes", "Wrap them in brackets", "Use a # sign"],
            correctIndex: 1,
            explanation: "String literals in SQL are typically enclosed in single quotes (e.g., 'New York')."
        }
    ],
    'ai-operator': [
        {
            id: 'ao1',
            text: "What is 'Prompt Engineering'?",
            options: ["Building AI hardware", "Writing effective instructions for AI models", "Programming robots", "Creating computer graphics"],
            correctIndex: 1,
            explanation: "Prompt engineering is the skill of crafting clear, effective prompts to get the best results from AI."
        },
        {
            id: 'ao2',
            text: "What does the 'Temperature' setting control in AI models?",
            options: ["Processing speed", "Randomness/creativity of responses", "Model accuracy", "Response length"],
            correctIndex: 1,
            explanation: "Temperature controls randomness - higher values give more creative but less predictable outputs."
        },
        {
            id: 'ao3',
            text: "What is RAG (Retrieval Augmented Generation)?",
            options: ["A type of AI virus", "Combining AI with external knowledge sources", "A programming language", "A graphics renderer"],
            correctIndex: 1,
            explanation: "RAG enhances AI responses by retrieving relevant information from external sources."
        },
        {
            id: 'ao4',
            text: "What is an LLM?",
            options: ["Large Language Model", "Low Latency Memory", "Long Learning Machine", "Linked Logic Module"],
            correctIndex: 0,
            explanation: "LLM stands for Large Language Model, broadly trained on text to generate human-like responses."
        },
        {
            id: 'ao5',
            text: "What is a 'Hallucination' in AI terms?",
            options: ["When the AI crashes", "When the AI confidently states incorrect information", "A graphical glitch", "When the AI works too fast"],
            correctIndex: 1,
            explanation: "Hallucinations occur when an AI generates false information presented as fact."
        },
        {
            id: 'ao6',
            text: "Why is context important for AI?",
            options: ["It isn't", "It helps the AI understand the specific situation and constraints", "It saves battery", "It changes the font"],
            correctIndex: 1,
            explanation: "Providing context helps the AI narrow down the vast possibilities to give a relevant answer."
        },
        {
            id: 'ao7',
            text: "What is a 'Token'?",
            options: ["A crypto coin", "A unit of text (part of a word) the AI processes", "A user login", "A standard error"],
            correctIndex: 1,
            explanation: "AI models process text in chunks called tokens, roughly equivalent to 0.75 words."
        },
        {
            id: 'ao8',
            text: "Which creates better images?",
            options: ["'Dog'", "'A cinematic photo of a golden retriever running in a park, golden hour lighting, 4k'"],
            correctIndex: 1,
            explanation: "Detailed, descriptive prompts usually yield much higher quality and more specific results."
        },
        {
            id: 'ao9',
            text: "Can current AI models 'think' like humans?",
            options: ["Yes, exactly like humans", "No, they predict the next likely word based on patterns", "Yes, they have feelings", "Only on Tuesdays"],
            correctIndex: 1,
            explanation: "LLMs are statistical prediction engines, not conscious thinking entities."
        },
        {
            id: 'ao10',
            text: "What is a 'System Prompt'?",
            options: ["An error message", "High-level instructions defining the AI's persona and rules", "The user's question", "The computer OS"],
            correctIndex: 1,
            explanation: "System prompts set the behavior, tone, and constraints for the AI session."
        }
    ],
    'automation': [
        {
            id: 'au1',
            text: "What triggers an automation to run?",
            options: ["Random chance", "An event or scheduled time", "User mood", "Weather conditions"],
            correctIndex: 1,
            explanation: "Automations are triggered by specific events (like a form submission) or scheduled times (cron jobs)."
        },
        {
            id: 'au2',
            text: "What is a Webhook?",
            options: ["A fishing tool", "A way for apps to send real-time data to other apps", "A type of virus", "A social media feature"],
            correctIndex: 1,
            explanation: "Webhooks are HTTP callbacks that send data to a URL when an event occurs."
        },
        {
            id: 'au3',
            text: "What does a Cron expression like '0 9 * * 1' mean?",
            options: ["Run every 9 minutes", "Run at 9 AM every Monday", "Run 9 times per day", "Delete all data at 9"],
            correctIndex: 1,
            explanation: "Cron expressions define schedules - this one means minute 0, hour 9, any day, any month, day 1 (Monday)."
        },
        {
            id: 'au4',
            text: "What is OAuth 2.0 used for?",
            options: ["Encrypting files", "Secure authorization between applications", "Compressing data", "Creating backups"],
            correctIndex: 1,
            explanation: "OAuth 2.0 is a standard for secure authorization, allowing apps to access data without sharing passwords."
        },
        {
            id: 'au5',
            text: "What is an 'API Key'?",
            options: ["A physical key", "A unique code that identifies and authenticates an application/user", "A password manager", "A database index"],
            correctIndex: 1,
            explanation: "API Keys act like passwords for programs, allowing access to services."
        },
        {
            id: 'au6',
            text: "In automation, what is an 'Action'?",
            options: ["The trigger", "The task performed after a trigger fires", "The user login", "The error log"],
            correctIndex: 1,
            explanation: "Triggers start the workflow; Actions are the steps the workflow performs (e.g., 'Send Email')."
        },
        {
            id: 'au7',
            text: "What is 'Polling'?",
            options: ["Asking for votes", "Repeatedly checking a system to see if new data exists", "Sending data once", "Ignoring errors"],
            correctIndex: 1,
            explanation: "Polling involves checking a resource at regular intervals, as opposed to waiting for a webhook push."
        },
        {
            id: 'au8',
            text: "What usually happens if an automation step fails?",
            options: ["The computer explodes", "The workflow stops and reports an error", "It skips it and pretends nothing happened", "It deletes the account"],
            correctIndex: 1,
            explanation: "Standard behavior is to halt execution and return an error status."
        },
        {
            id: 'au9',
            text: "What are 'Rate Limits'?",
            options: ["Speed limits for cars", "Restrictions on how many API requests you can make in a given time", "Prices for software", "File size limits"],
            correctIndex: 1,
            explanation: "Services impose rate limits to prevent abuse and ensure stability."
        },
        {
            id: 'au10',
            text: "What is JSON used for in automation?",
            options: ["Styling text", "Structuring data sent between services", "Video editing", "Audio compression"],
            correctIndex: 1,
            explanation: "JSON is the standard format for exchanging data payloads in web automations."
        }
    ],
    'ui-ux': [
        {
            id: 'ux1',
            text: "In CSS, what does the 'Box Model' describe?",
            options: ["3D graphics rendering", "How elements are sized with content, padding, border, and margin", "Image compression", "Font rendering"],
            correctIndex: 1,
            explanation: "The Box Model is fundamental to CSS layout - every element is a box with content, padding, border, and margin."
        },
        {
            id: 'ux2',
            text: "What is the purpose of 'Accessibility' in web design?",
            options: ["Making websites faster", "Ensuring everyone can use your website, including those with disabilities", "Adding more features", "Improving SEO only"],
            correctIndex: 1,
            explanation: "Accessibility ensures websites work for all users, including those using screen readers or with visual/motor impairments."
        },
        {
            id: 'ux3',
            text: "What does Flexbox help you do in CSS?",
            options: ["Add animations", "Create flexible, responsive layouts", "Load fonts", "Optimize images"],
            correctIndex: 1,
            explanation: "Flexbox is a CSS layout method that makes it easy to arrange items in rows or columns with flexible sizing."
        },
        {
            id: 'ux4',
            text: "What makes colors 'complementary' in color theory?",
            options: ["They are the same color", "They are opposite on the color wheel", "They are both dark", "They are both light"],
            correctIndex: 1,
            explanation: "Complementary colors sit opposite each other on the color wheel and create high contrast when paired."
        },
        {
            id: 'ux5',
            text: "What is 'White Space'?",
            options: ["Empty pages", "The empty space around elements that improves readability", "A mistake in coding", "White colored text"],
            correctIndex: 1,
            explanation: "White space (negative space) prevents clutter and guides the user's eye."
        },
        {
            id: 'ux6',
            text: "What is a 'Wireframe'?",
            options: ["A metal sculpture", "A low-fidelity visual guide representing the skeletal framework of a website", "A completed design", "A code structure"],
            correctIndex: 1,
            explanation: "Wireframes focus on structure and layout without getting distracted by design details like color."
        },
        {
            id: 'ux7',
            text: "What does 'Responsive Design' mean?",
            options: ["The website loads fast", "The website adapts to look good on different screen sizes (mobile, desktop)", "The website has voice control", "The tech support responds quickly"],
            correctIndex: 1,
            explanation: "Responsive design ensures usability across phones, tablets, and desktops using fluid grids/media queries."
        },
        {
            id: 'ux8',
            text: "What is 'Hierarchy' in typography?",
            options: ["A list of employees", "Using size, weight, and color to show importance order", "Using only uppercase", "Using random fonts"],
            correctIndex: 1,
            explanation: "Visual hierarchy guides the reader to the most important information first (e.g., H1 > H2 > p)."
        },
        {
            id: 'ux9',
            text: "What is a 'User Persona'?",
            options: ["A fake account", "A fictional character representing a user type to guide design decisions", "The designer's profile", "A login screen"],
            correctIndex: 1,
            explanation: "Personas help designers empathize with different user needs and goals."
        },
        {
            id: 'ux10',
            text: "What represents 'Contrast' in design?",
            options: ["Everything looking the same", "The difference between elements (like light vs dark) to create emphasis", "Using only one color", "Blurring the screen"],
            correctIndex: 1,
            explanation: "Contrast helps distinct elements stand out and improves text readability."
        }
    ],
    'sovereign-systems': [
        {
            id: 'ss1',
            text: "What is a Docker container?",
            options: ["A physical storage box", "An isolated environment to run applications", "A type of server", "A programming language"],
            correctIndex: 1,
            explanation: "Docker containers package applications with all their dependencies in isolated, portable environments."
        },
        {
            id: 'ss2',
            text: "What does a Reverse Proxy do?",
            options: ["Speeds up downloads", "Sits between clients and servers to route traffic", "Blocks all traffic", "Creates backups"],
            correctIndex: 1,
            explanation: "A reverse proxy receives requests and forwards them to appropriate backend servers, adding security and load balancing."
        },
        {
            id: 'ss3',
            text: "Why is server monitoring important?",
            options: ["To spy on users", "To detect issues before they cause downtime", "To slow down the server", "It's not important"],
            correctIndex: 1,
            explanation: "Monitoring helps you spot problems early, optimize performance, and maintain uptime."
        },
        {
            id: 'ss4',
            text: "What is the benefit of 'Self-Hosting'?",
            options: ["It is always free", "Control and privacy over your own data", "It requires no skills", "Faster internet"],
            correctIndex: 1,
            explanation: "Self-hosting gives you sovereignty over your data, avoiding reliance on third-party cloud providers."
        },
        {
            id: 'ss5',
            text: "What is a 'Port' in networking?",
            options: ["Where ships dock", "A communication endpoint for specific services (e.g., port 80 for Web)", "A type of cable", "A password"],
            correctIndex: 1,
            explanation: "Ports allow a single IP address to serve multiple services (Web, Email, SSH, etc.)."
        },
        {
            id: 'ss6',
            text: "What is a 'Volume' in Docker?",
            options: ["The sound level", "A mechanism to persist data outside the container", "A heavy file", "A network connection"],
            correctIndex: 1,
            explanation: "Volumes allow data to survive even if the container is deleted or recreated."
        },
        {
            id: 'ss7',
            text: "What usually runs on Port 443?",
            options: ["HTTP (Insecure)", "HTTPS (Secure Web Traffic)", "SSH", "Email"],
            correctIndex: 1,
            explanation: "Port 443 is the standard port for secure HTTPS web traffic."
        },
        {
            id: 'ss8',
            text: "What is 'Uptime'?",
            options: ["Time spent standing up", "The amount of time a server has been running and available", "Time of day", "Download speed"],
            correctIndex: 1,
            explanation: "Uptime is a key metric for server reliability."
        },
        {
            id: 'ss9',
            text: "Which command lists running Docker containers?",
            options: ["docker run", "docker ps", "docker stop", "docker list"],
            correctIndex: 1,
            explanation: "`docker ps` shows the process status of active containers."
        },
        {
            id: 'ss10',
            text: "What are Environment Variables used for?",
            options: ["Changing the weather", "Configuring applications (like passwords/keys) without hardcoding them", "Setting font size", "Speeding up CPU"],
            correctIndex: 1,
            explanation: "They keep configuration separate from code, which is crucial for security and flexibility."
        }
    ],
    'game-dev': [
        {
            id: 'gd1',
            text: "What is the 'Game Loop'?",
            options: ["A type of bug", "The cycle of update and render that runs every frame", "A marketing strategy", "A save file format"],
            correctIndex: 1,
            explanation: "The game loop continuously processes input, updates game state, and renders to screen - typically 60 times per second."
        },
        {
            id: 'gd2',
            text: "What is 'Collision Detection'?",
            options: ["Finding errors in code", "Determining when game objects overlap or touch", "Detecting cheaters", "Loading graphics"],
            correctIndex: 1,
            explanation: "Collision detection checks if game entities (sprites, characters, projectiles) are touching or overlapping."
        },
        {
            id: 'gd3',
            text: "What is a 'State Machine' in game development?",
            options: ["A broken computer", "A way to manage different states/behaviors of game entities", "A graphics card", "A type of controller"],
            correctIndex: 1,
            explanation: "State machines organize entity behavior into states (idle, walking, attacking) with defined transitions between them."
        },
        {
            id: 'gd4',
            text: "What does 'Juice' mean in game design?",
            options: ["Fruit-themed games", "Adding satisfying feedback like particles, sounds, and screen shake", "Energy drinks for developers", "Compressed game files"],
            correctIndex: 1,
            explanation: "'Juicing' a game means adding polish and feedback - particles, sounds, screen shake - that makes actions feel impactful."
        },
        {
            id: 'gd5',
            text: "What is a 'Sprite'?",
            options: ["A beverage", "A 2D image or animation used for characters/objects", "A 3D model", "A sound effect"],
            correctIndex: 1,
            explanation: "In 2D games, visual elements like characters and items are rendered using sprites."
        },
        {
            id: 'gd6',
            text: "What is 'FPS'?",
            options: ["First Person Shooter OR Frames Per Second", "Files Per Second", "Fast Processing System", "Format Pictures Soon"],
            correctIndex: 0,
            explanation: "It can mean a genre (First Person Shooter) or a performance metric (Frames Per Second). Context matters!"
        },
        {
            id: 'gd7',
            text: "What is a 'Physics Engine' responsible for?",
            options: ["Drawing graphics", "Simulating gravity, friction, and collisions", "Playing music", "Saving the game"],
            correctIndex: 1,
            explanation: "Physics engines calculate how objects move and interact according to physical laws."
        },
        {
            id: 'gd8',
            text: "What is 'Playtesting'?",
            options: ["Playing games for fun", "Observing players testing the game to find bugs and design flaws", "Developing the game", "Selling the game"],
            correctIndex: 1,
            explanation: "Playtesting is critical for validating that the game is actually fun and understandable to others."
        },
        {
            id: 'gd9',
            text: "What is a 'Hitbox'?",
            options: ["A boxing game", "The invisible shape used to calculate collisions for an object", "The game box", "A button"],
            correctIndex: 1,
            explanation: "Hitboxes are often simpler shapes (boxes/circles) around complex sprites used for efficient collision math."
        },
        {
            id: 'gd10',
            text: "What describes 'Level Design'?",
            options: ["Coding the engine", "Creating the detailed environments and challenges the player traverses", "Drawing the main character", "Composing music"],
            correctIndex: 1,
            explanation: "Level design determines flow, difficulty, and pacing of the gameplay experience."
        }
    ]
};
